import type { CustomCellRendererProps } from "ag-grid-react";
import { useEffect, useRef, useState } from "react";

interface Delta {
  text: string;
  up: boolean;
  highlight: boolean;
}

function displayText({ value, valueFormatted }: CustomCellRendererProps) {
  if (valueFormatted != null) return valueFormatted;
  return value == null ? "" : String(value);
}

/**
 * Flashes the change only when the shown text and the shown delta both move,
 * so float noise below display precision stays quiet. Reuses AG Grid's
 * `ag-value-change-*` classes.
 */
export function ChangeFlashCell(params: CustomCellRendererProps) {
  const { value, formatValue } = params;
  const text = displayText(params);
  const last = useRef<{ text: string; value: unknown } | null>(null);
  const [delta, setDelta] = useState<Delta | null>(null);

  useEffect(() => {
    const previous = last.current;
    last.current = { text, value };
    if (previous === null || previous.text === text) return;
    if (typeof value !== "number" || typeof previous.value !== "number") return;
    const change = value - previous.value;
    const deltaText =
      formatValue?.(Math.abs(change)) ?? String(Math.abs(change));
    if (deltaText === (formatValue?.(0) ?? "0")) return;
    setDelta({
      text: deltaText,
      up: change >= 0,
      highlight: Boolean(previous.value)
    });
  }, [text, value, formatValue]);

  useEffect(() => {
    if (delta === null) return;
    const timer = window.setTimeout(() => setDelta(null), 2000);
    return () => window.clearTimeout(timer);
  }, [delta]);

  const deltaClass = delta
    ? `ag-value-change-delta ag-value-change-delta-${delta.up ? "up" : "down"}`
    : "ag-value-change-delta";
  const valueClass = delta?.highlight
    ? "ag-value-change-value ag-value-change-value-highlight"
    : "ag-value-change-value";
  return (
    <span>
      <span className={deltaClass}>
        {delta ? `${delta.up ? "↑" : "↓"}${delta.text}` : null}
      </span>
      <span className={valueClass}>{text}</span>
    </span>
  );
}
